<script>
import { required, minValue } from 'vuelidate/lib/validators';
import Repositories from '@/repositories';
const ItemsRepository = Repositories.get('items');
import 'vue-loading-overlay/dist/vue-loading.css';
import Loading from 'vue-loading-overlay';
import scrollMixin from '@/mixins/scrollWheelMixin';
import Tooltip from '@/components/widgets/errors/validations-tooltip.vue';

/**
 * Dashboard Component
 */
export default {
  components: {
    Loading,
    Tooltip,
  },
  data() {
    return {
      isLoading: false,
      form: {
        code: '',
        name: '',
        quantity: 0,
        price: 0,
        supplier: null,
      },
    };
  },
  mixins: [scrollMixin],
  methods: {
    addCustomItem() {
      this.$v.form.$touch();
      if (!this.$v.form.$invalid) {
        this.isLoading = true;

        let payload = {
          code: this.form.code,
          name: this.form.name,
          supplierIdPublic: this.form.supplier,
          itemSuppliers: [
            {
              code: this.form.code,
              name: this.form.name,
              supplierIdPublic: this.form.supplier,
              price: this.form.price,
            },
          ],
          isCustomItem: true,
        };

        this.$v.$reset();

        ItemsRepository.create(payload)
          .then(({ data }) => {
            this.$emit('item-to-add', {
              item: data,
              quantity: +this.form.quantity,
            });

            this.form = {
              code: '',
              name: '',
              quantity: 0,
              price: 0,
              supplier: null,
            };

            this.isLoading = false;
          })
          .catch(() => {
            this.$toast.errorToast(this.$t('errors.errorLoading'));
            this.isLoading = false;
          });
      }
    },
    wheelEditQuantity(event) {
      this.form.quantity = this.scrollMixinEvent(event, this.form.quantity, 0);
    },
    wheelPrice(event) {
      this.form.price = this.scrollMixinEvent(event, this.form.price, 0);
    },
  },
  computed: {
    suppliers() {
      return this.$store.state.cache.suppliers;
    },
  },
  validations() {
    return {
      form: {
        code: { required },
        name: { required },
        quantity: { required, minValue: minValue(0.00001) },
        price: { required },
        supplier: { required },
      },
    };
  },
};
</script>

<template>
  <div>
    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>
    <div class="d-flex">
      <b-form-group
        class="pr-3"
        :label="$t('general.code')"
        style="width: 150px; margin-bottom: 0"
      >
        <b-form-input
          :class="{
            'is-invalid': $v.form.code.$error,
          }"
          type="text"
          v-model="form.code"
          :placeholder="$t('general.code')"
          @keydown.enter.prevent="addCustomItem"
        >
        </b-form-input>

        <Tooltip :validations="$v.form.code" />
      </b-form-group>

      <b-form-group
        style="margin-bottom: 0"
        class="pr-3 flex-grow-1"
        :label="$t('general.name')"
      >
        <b-form-input
          :class="{
            'is-invalid': $v.form.name.$error,
          }"
          type="text"
          v-model="form.name"
          :placeholder="$t('general.name')"
          @keydown.enter.prevent="addCustomItem"
        >
        </b-form-input>

        <Tooltip :validations="$v.form.name" />
      </b-form-group>

      <b-form-group
        style="margin-bottom: 0"
        class="pr-3 quantity-group"
        :label="$t('items.quantity')"
      >
        <b-form-input
          :class="{
            'is-invalid': $v.form.quantity.$error,
          }"
          type="number"
          step="0.0001"
          v-model="form.quantity"
          :placeholder="$t('items.quantity')"
          @keydown.enter.prevent="addCustomItem"
          @wheel.prevent="wheelEditQuantity($event)"
        >
        </b-form-input>

        <Tooltip :validations="$v.form.quantity" />
      </b-form-group>

      <b-form-group
        style="margin-bottom: 0"
        class="pr-3 quantity-group"
        :label="$t('items.price')"
      >
        <b-form-input
          :class="{
            'is-invalid': $v.form.price.$error,
          }"
          type="number"
          step="0.01"
          v-model="form.price"
          :placeholder="$t('items.price')"
          @keydown.enter.prevent="addCustomItem"
          @wheel.prevent="wheelPrice($event)"
        >
        </b-form-input>

        <Tooltip :validations="$v.form.price" />
      </b-form-group>

      <b-form-group
        style="margin-bottom: 0"
        class="pr-3"
        :label="$t('suppliers.supplier')"
        label-for="supplier-input"
      >
        <b-form-select
          style="height: 40px"
          :class="{
            'is-invalid': $v.form.supplier.$error,
          }"
          v-model="form.supplier"
          :options="suppliers"
          :validator="$v.form.supplier"
          text-field="name"
          value-field="idPublic"
          @keydown.enter.prevent="addCustomItem"
        >
        </b-form-select>

        <Tooltip :validations="$v.form.supplier" />
      </b-form-group>

      <b-form-group style="margin-bottom: 0" class="align-bottom mt-auto">
        <b-button
          @click="addCustomItem"
          block
          style="height: 40px; margin-top: 27.5px"
          variant="primary"
          >{{ $t('general.add') }}</b-button
        >
      </b-form-group>
    </div>
  </div>
</template>

<style scoped>
.quantity-group {
  min-width: 100px;
  max-width: 100px;
}
</style>
